/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AppleSignin from 'react-apple-signin-auth';
import { jwtDecode } from 'jwt-decode';
import { setTokenInApi } from '../../utils/User/user-openapi-hooks';
import { loginUser, loginUserWithEmailOtp, setUserState } from '../../store/navBar/loginSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button } from '@mui/material';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { UserToken } from '../../utils/User/UserToken';
import Badge from '../presentationalComponents/Badge';
import { requestEmailOtpLogin } from '../../utils/User/user-openapi-hooks';

type AppleAuthResponse = {
    authorization: {
        code: string;
        id_token: string;
    };
    user?: {
        email: string;
        name: {
            firstName: string;
            lastName: string;
        };
    };
};

type AppleIdTokenPayload = {
    iss: string;
    sub: string;
    aud: string;
    exp: number;
    iat: number;
    email: string;
    email_verified: 'true' | 'false';
    is_private_email: 'true' | 'false';
    auth_time: number;
};

const AppleLogo = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.365 1.43c0 1.14-.455 2.224-1.274 3.059-.85.864-2.03 1.535-3.245 1.44-.073-1.18.465-2.368 1.3-3.14.83-.767 2.198-1.345 3.22-1.36 0 .001 0 .001-.001.001zm4.406 17.7c-.544 1.25-.795 1.77-1.48 2.86-1.03 1.58-2.48 3.54-4.29 3.55-1.71.01-2.16-1.15-4.5-1.14-2.34.01-2.83 1.15-4.54 1.13-1.82-.01-3.26-1.79-4.29-3.37-2.94-4.49-3.25-9.76-1.43-12.53 1.26-1.87 3.27-2.97 5.16-2.97 2.01 0 3.28 1.2 4.9 1.22 1.6.02 2.6-1.22 4.9-1.22 1.49 0 3.06.81 4.03 2.2-3.51 1.93-2.95 7 .51 8.76z" />
    </svg>
);

const Form = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userHasAccount = useAppSelector((state) => state.login.userIsRegistered);
    const [showWarningNoAccountFound, setShowWarningNoAccountFound] = useState(false);
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [otpRequested, setOtpRequested] = useState(false);
    const [emailLoginMessage, setEmailLoginMessage] = useState('');
    const [emailLoginError, setEmailLoginError] = useState('');
    const [isRequestingOtp, setIsRequestingOtp] = useState(false);
    const [isEmailLoginPending, setIsEmailLoginPending] = useState(false);
    const location = useLocation();
    const from = location.state?.from || '/';

    useEffect(() => {
        if (userHasAccount) {
            navigate(from);
        }
    }, [from, navigate, userHasAccount]);

    // ✅ Google Login
    const handleGoogleSuccess = async (response: CredentialResponse) => {
        const identityToken = response.credential;

        if (!identityToken) {
            console.error("Kein Google Token erhalten.");
            return;
        }

        const userObject: UserToken = jwtDecode(identityToken);
        setTokenInApi(identityToken);
        dispatch(
            setUserState({
                userName: userObject.name,
                token: identityToken,
                picture: userObject.picture,
                loginFirstname: userObject.given_name,
                loginLastname: userObject.family_name,
                loginEmail: userObject.email,
            })
        );
        const loginResult = await dispatch(loginUser(identityToken));
        if (!loginResult.payload) {
            setShowWarningNoAccountFound(true);
        }
    };

    const handleEmailOtpRequest = async () => {
        if (!email.trim()) {
            setEmailLoginError('Bitte gib deine E-Mail-Adresse ein.');
            setEmailLoginMessage('');
            return;
        }

        try {
            setIsRequestingOtp(true);
            setEmailLoginError('');
            setEmailLoginMessage('');
            await requestEmailOtpLogin(email.trim());
            setOtpRequested(true);
            setEmailLoginMessage('Wir haben dir einen 6-stelligen Code per E-Mail geschickt.');
        } catch (error) {
            setEmailLoginError('Der Code konnte nicht angefordert werden. Bitte pruefe deine E-Mail-Adresse.');
        } finally {
            setIsRequestingOtp(false);
        }
    };

    const handleEmailOtpLogin = async () => {
        if (!email.trim()) {
            setEmailLoginError('Bitte gib deine E-Mail-Adresse ein.');
            return;
        }

        if (!/^\d{6}$/.test(otp)) {
            setEmailLoginError('Bitte gib einen 6-stelligen Zahlencode ein.');
            return;
        }

        try {
            setIsEmailLoginPending(true);
            setEmailLoginError('');
            setShowWarningNoAccountFound(false);
            const loginPayload = await dispatch(loginUserWithEmailOtp({ email: email.trim(), otp })).unwrap();

            dispatch(
                setUserState({
                    userName: email.trim(),
                    token: loginPayload.identityToken ?? '',
                    picture: '',
                    loginFirstname: '',
                    loginLastname: '',
                    loginEmail: email.trim(),
                })
            );
        } catch (error) {
            const backendMessage = error instanceof Error ? error.message : '';
            if (backendMessage.includes('NOT_FOUND') || backendMessage.toLowerCase().includes('not found')) {
                setEmailLoginError('Zu dieser E-Mail-Adresse wurde kein Account gefunden. Bitte registriere dich zuerst.');
            } else if (backendMessage.includes('No OTP was requested')) {
                setEmailLoginError('Es wurde kein gueltiger Code angefordert. Bitte fordere einen neuen Code an.');
            } else if (backendMessage.includes('OTP expired')) {
                setEmailLoginError('Der Code ist abgelaufen. Bitte fordere einen neuen Code an.');
            } else if (backendMessage.includes('Invalid OTP')) {
                setEmailLoginError('Der Code ist ungueltig. Bitte pruefe den 6-stelligen Code oder fordere einen neuen an.');
            } else {
                setEmailLoginError('Der Code ist ungueltig oder abgelaufen. Bitte fordere einen neuen Code an.');
            }
        } finally {
            setIsEmailLoginPending(false);
        }
    };

    // ✅ Apple Login (vorbereitet, derzeit deaktiviert)
    const handleAppleSuccess = async (response: AppleAuthResponse) => {
        const identityToken = response.authorization?.id_token;
        if (!identityToken) {
            console.error("Kein Apple Token erhalten.");
            return;
        }

        const decoded: AppleIdTokenPayload = jwtDecode(identityToken);

        setTokenInApi(identityToken);
        dispatch(
            setUserState({
                userName: response.user?.name?.firstName + ' ' + response.user?.name?.lastName ?? '',
                token: identityToken,
                picture: '',
                loginFirstname: response.user?.name?.firstName ?? '',
                loginLastname: response.user?.name?.lastName ?? '',
                loginEmail: decoded.email,
            })
        );

        const loginResult = await dispatch(loginUser(identityToken));
        if (!loginResult.payload) {
            setShowWarningNoAccountFound(true);
        }
    };

    return (
        <Box>
            <Box marginBottom={4}>
                <Typography
                    sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}
                    gutterBottom
                    color={'text.secondary'}
                >
                    Login
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Willkommen
                </Typography>
                <Typography color="text.secondary">Bitte logge dich ein.</Typography>
            </Box>

            {/* ✅ Google Login */}
            <Box marginBottom={2}>
                <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => console.error('Google Login fehlgeschlagen')}
                />
            </Box>

            <Box marginBottom={3} marginTop={3}>
                <Divider>oder</Divider>
            </Box>

            <Stack spacing={2} marginBottom={4}>
                <TextField
                    label="E-Mail"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    fullWidth
                />
                <Button
                    variant="outlined"
                    onClick={handleEmailOtpRequest}
                    disabled={isRequestingOtp}
                >
                    {isRequestingOtp ? 'Code wird gesendet...' : 'OTP per E-Mail anfordern'}
                </Button>
                <TextField
                    label="6-stelliger OTP-Code"
                    value={otp}
                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                    disabled={!otpRequested && !emailLoginMessage}
                    fullWidth
                />
                <Button
                    variant="contained"
                    onClick={handleEmailOtpLogin}
                    disabled={isEmailLoginPending}
                >
                    {isEmailLoginPending ? 'Login laeuft...' : 'Mit E-Mail und OTP einloggen'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                    Der OTP ist ein 6-stelliger Zahlencode und bleibt nur fuer diesen Login gueltig.
                </Typography>
                {emailLoginMessage && <Alert severity="success">{emailLoginMessage}</Alert>}
                {emailLoginError && <Alert severity="error">{emailLoginError}</Alert>}
            </Stack>

            {/* 🟡 Apple Login – deaktiviert */}
            <Box marginBottom={4}>
                <AppleSignin
                    authOptions={{
                        clientId: 'com.example.auth',
                        scope: 'email name',
                        redirectURI: 'https://yourapp.com/auth/apple/callback',
                        state: 'state',
                        usePopup: true,
                    }}
                    uiType="light"
                    onSuccess={handleAppleSuccess}
                    onError={(error: unknown) => {
                        console.error('Apple Sign In Error:', error);
                    }}
                    render={() => (
                        <Button
                            disabled
                            variant="outlined"
                            fullWidth
                            sx={{ textTransform: 'none' }}
                            startIcon={<AppleLogo />}
                        >
                            Mit Apple einloggen <Badge type="soon" />
                        </Button>
                    )}
                />
            </Box>

            {showWarningNoAccountFound && (
                <Alert
                    severity="warning"
                    action={
                        <Button
                            component={Link}
                            color="inherit"
                            size="small"
                            to="/register/vanlifer"
                        >
                            Registrieren
                        </Button>
                    }
                >
                    Es existiert noch kein Account mit diesem Login.
                </Alert>
            )}

            <Box>
                <Typography variant="body2" color="text.secondary">
                    Noch keinen Account? <Link to="/register/vanlifer">Registrieren</Link>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Möchtest du Gastgeber werden? <Link to="/register/host">Mehr Infos</Link>
                </Typography>
            </Box>
        </Box>
    );
};

export default Form;
